import { useState } from "react";
import PropTypes from "prop-types";

import { browserLocale } from "utils/time";

/* CSS-grid heatmap (port of the design prototype's Heatmap in viz2.jsx).
   rowLabels x colLabels, `matrix` is rowLabels.length arrays of colLabels.length
   numbers (null/undefined cells render blank). Cells are tinted from --surface-3
   up to --accent by value; a hovered cell gets an outline and a native tooltip. */

const fmt = (v, d = 3) =>
  v == null || Number.isNaN(v)
    ? "—"
    : Number(v).toLocaleString(browserLocale(), {
        maximumFractionDigits: d,
        minimumFractionDigits: d,
      });

export default function Heatmap({ rowLabels, colLabels, matrix, unit, rowLabelWidth }) {
  const [hi, setHi] = useState(null);

  const values = matrix.flat().filter((v) => v != null && !Number.isNaN(v));
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const span = max - min || 1;

  const color = (v) => {
    if (v == null || Number.isNaN(v)) return "var(--surface-2)";
    const ratio = (v - min) / span;
    return `color-mix(in srgb, var(--accent) ${Math.round(12 + ratio * 78)}%, var(--surface-3))`;
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${rowLabelWidth}px repeat(${colLabels.length}, minmax(20px, 1fr))`,
          gap: 3,
          minWidth: 520,
        }}
      >
        <div />
        {colLabels.map((c, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            style={{
              fontSize: 9.5,
              color: "var(--ink-3)",
              textAlign: "center",
              fontFamily: "var(--mono)",
            }}
          >
            {c}
          </div>
        ))}
        {rowLabels.map((rl, r) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={r} style={{ display: "contents" }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--ink-2)",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: 6,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={rl}
            >
              {rl}
            </div>
            {(matrix[r] || []).map((v, c) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={c}
                title={`${rl} · ${colLabels[c]}: ${fmt(v)}${unit}`}
                onMouseEnter={() => setHi(`${r}-${c}`)}
                onMouseLeave={() => setHi(null)}
                style={{
                  height: 26,
                  borderRadius: 5,
                  background: color(v),
                  cursor: "pointer",
                  outline: hi === `${r}-${c}` ? "2px solid var(--ink)" : "none",
                  transition: "outline .1s",
                }}
              />
            ))}
          </div>
        ))}
      </div>
      {values.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 14,
            justifyContent: "flex-end",
          }}
        >
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
            {fmt(min, 2)}
            {unit}
          </span>
          <div
            style={{
              width: 120,
              height: 9,
              borderRadius: 5,
              background:
                "linear-gradient(90deg, color-mix(in srgb, var(--accent) 12%, var(--surface-3)), var(--accent))",
            }}
          />
          <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
            {fmt(max, 2)}
            {unit}
          </span>
        </div>
      )}
    </div>
  );
}

Heatmap.propTypes = {
  rowLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  colLabels: PropTypes.arrayOf(PropTypes.node).isRequired,
  matrix: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
  unit: PropTypes.string,
  rowLabelWidth: PropTypes.number,
};

Heatmap.defaultProps = {
  unit: "",
  rowLabelWidth: 70,
};
